import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useRef, useState } from "react"
import type { Resolver } from "react-hook-form"
import { FaPlus } from "react-icons/fa"

import { useCreateSession } from "@/hooks/api/useSessions"
import { usePermissions } from "@/hooks/usePermissions"
import { sessionUpdateSchema } from "@/types/session"
import type { AddSessionProps, SessionCreateInput, SessionUpdateInput } from "@/types/session"
import { Button } from "@chakra-ui/react"

import SessionFormDialog from "./SessionFormDialog"

const AddSession = memo(({ onSuccess, onCancel }: AddSessionProps = {}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { hasPermission } = usePermissions()
  const createSessionMutation = useCreateSession()
  const addButtonRef = useRef<HTMLButtonElement | null>(null)

  if (!hasPermission("sessions:create")) {
    return null
  }

  const defaultValues = useMemo<SessionUpdateInput>(
    () => ({
      name: "",
      slug: "",
      description: "",
      session_type: "regular_season",
      start_week: "",
      end_week: "",
      registration_opens: "",
      registration_closes: "",
      early_bird_deadline: "",
      makeup_classes_allowed: 2,
      total_classes: null,
      no_class_dates: null,
      is_active: true,
      is_published: false,
      notes: "",
    }),
    [],
  )

  const handleClose = () => {
    setIsOpen(false)
    onCancel?.()
  }

  const handleSubmit = async (data: SessionUpdateInput) => {
    // Cast to CreateInput since all required fields will be present after form validation
    await createSessionMutation.mutateAsync(data as SessionCreateInput)
    onSuccess?.()
  }

  return (
    <>
      <Button
        ref={addButtonRef}
        onClick={() => setIsOpen(true)}
        colorPalette="blue"
        size="sm"
        aria-label="Add new session"
      >
        <FaPlus fontSize="14px" aria-hidden="true" />
        Add Season
      </Button>

      <SessionFormDialog
        isOpen={isOpen}
        title="Add New Season"
        submitLabel="Create Season"
        /* TODO: zodResolver generic mismatch - UpdateSchema infers UpdateInput but dialog expects Resolver<UpdateInput> */
        resolver={zodResolver(sessionUpdateSchema) as Resolver<SessionUpdateInput>}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onClose={handleClose}
        isSubmitting={createSessionMutation.isPending}
        finalFocusRef={addButtonRef}
        showSkipLink
        loadingText="Creating..."
      />
    </>
  )
})

AddSession.displayName = "AddSession"

export default AddSession
export type { AddSessionProps }
